import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../shared/api/client';

/**
 * Altcha proof-of-work captcha (section 9.2).
 *
 * The widget is a web component (`<altcha-widget>`) from the MIT-licensed
 * `altcha` package, loaded lazily: the bundle is a quarter of a megabyte of
 * Svelte plus a Web Worker, and a visitor who never opens a form must not pay
 * for it.
 *
 * Four details are load-bearing:
 *
 *   1. **The widget element is created once and never touched by React again.**
 *      It manages its own subtree, so React must not render anything inside its
 *      container: replacing React-owned children throws
 *      `Failed to execute 'removeChild' on 'Node'` and blanks the page — which is
 *      exactly what an earlier version of this component did.
 *   2. **The challenge comes from our own API** (`GET /api/v1/auth/captcha`),
 *      never from a third party. Altcha verifies the proof-of-work
 *      cryptographically on our server, so no visitor data leaves the
 *      deployment.
 *   3. **The widget writes a hidden input** named `captcha` with the base64
 *      payload; the form posts it as the `captcha` field. Submitting before
 *      solving leaves the field absent, and the server rejects that on purpose.
 *   4. **The language follows the interface**, re-configured on every language
 *      change, because the widget's own strings ("I'm not a robot", "Verifying…")
 *      are words the user reads (section 10.2).
 */

/** The `altcha` package calls Latin-American Spanish `es-419`. */
const ALTCHA_LANGUAGE: Record<string, string> = {
  en: 'en',
  es: 'es-419',
  'pt-BR': 'pt-br',
  ru: 'ru',
};

/** The i18n modules register themselves on import; load the one we need. */
const ALTCHA_I18N: Record<string, () => Promise<unknown>> = {
  en: () => import('altcha/i18n/en'),
  es: () => import('altcha/i18n/es-419'),
  'pt-BR': () => import('altcha/i18n/pt-br'),
  ru: () => import('altcha/i18n/ru'),
};

interface AltchaWidgetElement extends HTMLElement {
  configure?: (config: Record<string, unknown>) => Promise<void>;
}

/** Load the widget and its stylesheet once per document. */
let widgetPromise: Promise<void> | null = null;

async function loadAltcha(): Promise<void> {
  widgetPromise ??= (async () => {
    await import('altcha/altcha.css');
    await import('altcha');
  })();
  return widgetPromise;
}

/** The widget's default hidden field name is `altcha`; the API expects `captcha`. */
const FIELD_NAME = 'captcha';

export function CaptchaField(): React.ReactNode {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetRef = useRef<AltchaWidgetElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  const language = i18n.language;
  const altchaLanguage = ALTCHA_LANGUAGE[language] ?? ALTCHA_LANGUAGE.en;

  // Create the widget once. React renders the container and nothing else inside
  // it: the custom element owns its own DOM from that point on.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await loadAltcha();
        await (ALTCHA_I18N[language] ?? ALTCHA_I18N.en)();
      } catch {
        // A blocked script (CSP, offline, an aggressive extension) must not make
        // the form unusable: the field reports that it is unavailable and the
        // server decides what to do with a missing payload.
        if (!cancelled) setFailed(true);
        return;
      }
      const container = containerRef.current;
      if (cancelled || !container) return;

      const element = document.createElement('altcha-widget') as AltchaWidgetElement;
      element.setAttribute('challenge', `${API_BASE}/auth/captcha`);
      element.setAttribute('name', FIELD_NAME);
      element.setAttribute('language', altchaLanguage);
      // The visitor clicks the checkbox themselves; verifying on load would
      // spend a proof-of-work on every page view.
      element.setAttribute('auto', 'off');
      element.setAttribute('display', 'standard');
      element.setAttribute('hidefooter', '');

      container.appendChild(element);
      widgetRef.current = element;
      setReady(true);
    })();

    return () => {
      cancelled = true;
      // Remove the element ourselves so React and the custom element never
      // disagree about who owns the container's children.
      widgetRef.current?.remove();
      widgetRef.current = null;
    };
    // The widget is created once for the life of the form; the language effect
    // below re-configures it instead of re-creating it, so a solved challenge
    // survives a language switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow the interface language without discarding a solved challenge.
  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget?.configure) return;
    void (async () => {
      try {
        await (ALTCHA_I18N[language] ?? ALTCHA_I18N.en)();
        await widget.configure?.({ language: altchaLanguage });
      } catch {
        // Leave the previous language; the challenge itself is unaffected.
      }
    })();
  }, [language, altchaLanguage]);

  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3">
      <p className="mb-2 text-xs font-medium text-[var(--color-muted-fg)]">{t('captcha.label')}</p>
      {failed ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">{t('captcha.unavailable')}</p>
      ) : (
        <>
          {!ready && (
            <p className="text-xs text-[var(--color-muted-fg)]">{t('captcha.loading')}</p>
          )}
          {/* The custom element is appended here imperatively; this div must keep
              no React children of its own. */}
          <div ref={containerRef} data-testid="captcha-widget" />
        </>
      )}
    </div>
  );
}
